import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  Pencil,
  Pin,
  PinOff,
  Trash2,
} from 'lucide-react';
import { announcementsApi } from '@/services/api';
import { sanitizeAnnouncementHtml } from '@/utils/sanitizeHtml';
import {
  DescList,
  PageHeader,
  Section,
  LoadingState,
  ErrorState,
} from '@/components/common';

const TARGET_LABEL: Record<string, string> = {
  BUSINESS: '비즈니스앱',
  GUARDIAN: '보호자앱',
};

const CATEGORY_LABEL: Record<string, string> = {
  SYSTEM: '시스템 업데이트',
  MAINTENANCE: '점검 안내',
  EVENT: '이벤트',
  GENERAL: '일반 공지',
};

export function AnnouncementDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const numericId = Number(id);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['announcement', numericId],
    queryFn: () => announcementsApi.getAnnouncementDetail(numericId),
    enabled: Number.isFinite(numericId),
    staleTime: 30 * 1000,
  });

  const deleteMutation = useMutation({
    mutationFn: () => announcementsApi.deleteAnnouncement(numericId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['announcements'] });
      alert('공지사항이 삭제되었습니다.');
      navigate('/announcements');
    },
    onError: err => {
      alert(err instanceof Error ? err.message : '삭제 실패');
    },
  });

  const flagsMutation = useMutation({
    mutationFn: (flags: { isImportant?: boolean; isPinned?: boolean }) =>
      announcementsApi.updateAnnouncementFlags(numericId, flags),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['announcement', numericId] });
      await queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
    onError: err => {
      alert(err instanceof Error ? err.message : '변경 실패');
    },
  });

  if (isLoading) {
    return <LoadingState />;
  }
  if (error) {
    return <ErrorState error={error} onRetry={() => refetch()} />;
  }
  if (!data) {
    return <ErrorState error={new Error('공지사항을 찾을 수 없습니다.')} />;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="공지사항 상세"
        right={
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft size={16} />
              뒤로가기
            </button>
            <button
              type="button"
              onClick={() => navigate(`/announcements/${data.id}/edit`)}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Pencil size={16} />
              수정
            </button>
            <button
              type="button"
              onClick={() =>
                flagsMutation.mutate({ isImportant: !data.isImportant })
              }
              disabled={flagsMutation.isPending}
              className={`inline-flex items-center gap-1 rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-60 ${
                data.isImportant
                  ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <AlertCircle size={16} />
              {data.isImportant ? '중요 해제' : '중요 표시'}
            </button>
            <button
              type="button"
              onClick={() => flagsMutation.mutate({ isPinned: !data.isPinned })}
              disabled={flagsMutation.isPending}
              className={`inline-flex items-center gap-1 rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-60 ${
                data.isPinned
                  ? 'border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {data.isPinned ? <PinOff size={16} /> : <Pin size={16} />}
              {data.isPinned ? '상단 고정 해제' : '상단 고정'}
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('해당 공지사항을 삭제하시겠습니까?')) {
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
              className="inline-flex items-center gap-1 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              <Trash2 size={16} />
              {deleteMutation.isPending ? '삭제 중...' : '삭제'}
            </button>
          </div>
        }
      />

      <Section title="기본 정보">
        <DescList
          items={[
            { label: '공지 ID', value: data.id },
            {
              label: '대상 앱',
              value: TARGET_LABEL[data.targetApp] ?? data.targetApp,
            },
            {
              label: '카테고리',
              value: CATEGORY_LABEL[data.category] ?? data.category,
            },
            {
              label: '상단 고정',
              value: data.isPinned ? (
                <span className="inline-flex items-center gap-1 font-semibold text-primary-700">
                  <Pin size={14} /> 고정
                </span>
              ) : (
                <span className="text-gray-500">미고정</span>
              ),
            },
            {
              label: '중요 공지',
              value: data.isImportant ? (
                <span className="inline-flex items-center gap-1 font-semibold text-red-600">
                  <AlertCircle size={14} /> 중요
                </span>
              ) : (
                <span className="text-gray-500">일반</span>
              ),
            },
            {
              label: '게시 기간',
              value: (() => {
                const fmt = (dt?: string | null) =>
                  dt ? dt.slice(0, 16).replace('T', ' ') : '제한 없음';
                if (!data.displayStartAt && !data.displayEndAt)
                  return '제한 없음';
                return `${fmt(data.displayStartAt)} ~ ${fmt(data.displayEndAt)}`;
              })(),
            },
            { label: '등록일', value: data.createdAt },
            {
              label: '최종 수정일',
              value: data.updatedAt ?? '-',
            },
          ]}
        />
      </Section>

      <Section title="공지 내용">
        <div className="space-y-3">
          <h3 className="text-base font-semibold text-gray-900">{data.title}</h3>
          <div
            className="announcement-rich-content rounded-md border border-gray-100 bg-gray-50 p-3"
            dangerouslySetInnerHTML={{
              __html: sanitizeAnnouncementHtml(data.contentHtml),
            }}
          />
        </div>
      </Section>
    </div>
  );
}
